import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import algolia from '../../../utilities/algolia';
import firebase from '../../../utilities/firebase';
import store from '../../../utilities/store';
import { StateManager } from '../../../utilities/stateManager';
import constants from '../../../utilities/constants';
import ModalStackContext from '../../../utilities/ModalStackContext';
import CustomerSelectList from '../../../components/CustomerSelectList';
import NewCustomerModal from '../../../components/NewCustomerModal';

const STATUS_COLORS = {
  pending: '#ff9800',
  reviewed: '#2196f3',
  sent: '#9c27b0',
  approved: '#4caf50',
  declined: '#f44336',
};

export default function CustomerCard({ customer = {}, stockNumber, type, disabled }) {
  const safe = customer && typeof customer === 'object' ? customer : {};
  const initialName = safe.display_name || '';

  const [inputValue, setInputValue] = React.useState(initialName);
  const [committedName, setCommittedName] = React.useState(initialName);
  const [results, setResults] = React.useState([]);
  const [selected, setSelected] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [newCustomerOpen, setNewCustomerOpen] = React.useState(false);
  const [pendingName, setPendingName] = React.useState('');
  const inputWrapperRef = React.useRef(null);

  // Credit-app menu (opened from the buyer/co-buyer card)
  const stackZ = React.useContext(ModalStackContext);
  const [appsAnchor, setAppsAnchor] = React.useState(null);
  const [creditApps, setCreditApps] = React.useState([]);
  const [appsLoading, setAppsLoading] = React.useState(false);

  const openCreditAppsMenu = async (e) => {
    setAppsAnchor(e.currentTarget);
    const cid = safe.customer || safe.id;
    if (!cid) return;
    setAppsLoading(true);
    try {
      setCreditApps(await store.getCreditAppsForCustomer(cid));
    } catch (err) {
      setCreditApps([]);
      StateManager.setAlertAndOpen('Could not load credit apps', 'error');
    } finally {
      setAppsLoading(false);
    }
  };

  React.useEffect(() => {
    setInputValue(safe.display_name || '');
    setCommittedName(safe.display_name || '');
  }, [safe.display_name, safe.id]);

  const search = async (query) => {
    if (!query) { setResults([]); return; }
    const index = algolia.client.initIndex('customers');
    const { hits } = await index.search(query);
    const mapped = hits.map(hit => ({ ...hit, action: makeSelectAction(hit), label: getLabel(hit) }));
    setResults([{ label: 'Make New Customer', action: () => openNewCustomer(query) }, ...mapped]);
  };

  const makeSelectAction = (hit) => async () => {
    try {
      setLoading(true);
      const name = `${hit.first_name || ''}${hit.last_name ? ' ' : ''}${hit.last_name || ''}`;
      await setCustomer(stockNumber, type, hit.objectID);
      setInputValue(name);
      setCommittedName(name);
      setResults([]);
    } catch (e) {
      console.error(e);
      setInputValue(committedName);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const openNewCustomer = (rawName) => {
    setPendingName(rawName);
    setResults([]);
    setNewCustomerOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') results[selected]?.action?.();
    if (e.key === 'Escape') { setInputValue(committedName); setResults([]); }
    if (e.key === 'ArrowUp') setSelected(s => Math.max(0, s - 1));
    if (e.key === 'ArrowDown') setSelected(s => Math.min(results.length - 1, s + 1));
  };

  const handleBlur = () => {
    setTimeout(() => { setInputValue(committedName); setResults([]); }, 200);
  };

  const handleClear = async () => {
    if (!window.confirm('Remove this customer from the deal?')) return;
    setInputValue(''); setCommittedName(''); setResults([]);
    await removeCustomer(stockNumber, type);
  };

  const hasCustomer = !!safe.display_name;

  if (hasCustomer) {
    return (
      <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PersonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
              {safe.display_name}
            </Typography>
            {safe.email ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {safe.email}
              </Typography>
            ) : (
              // The doc gate reads this exact record's email, so show the gap
              // here instead of letting "Missing Buyer Email" surprise the rep
              // at send time.
              <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600, display: 'block' }}>
                No email — required to send docs
              </Typography>
            )}
            {safe.phone_number && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {safe.phone_number}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title="Credit applications">
            <IconButton size="small" onClick={openCreditAppsMenu} data-sold-ok>
              <DescriptionIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" data-sold-ok onClick={(e) => {
            // Always the doc the deal points at (safe.id). `safe.customer` is
            // normally a self-reference, but on a few records it points at a
            // duplicate party doc — editing that one saves the email somewhere
            // the deal never reads, so docs stay blocked on "Missing Buyer Email".
            const cid = safe.id;
            if (!cid) return;
            if (e.ctrlKey || e.metaKey) {
              window.open(`${window.location.pathname}?customer=${cid}`, '_blank');
              return;
            }
            StateManager.openCustomerModal(cid);
          }} title="Edit customer">
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
          {!disabled && (
            <IconButton size="small" onClick={handleClear} title="Remove customer">
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        <Menu
          anchorEl={appsAnchor}
          open={!!appsAnchor}
          onClose={() => setAppsAnchor(null)}
          sx={{ zIndex: stackZ + 1 }}
          MenuListProps={{ dense: true, sx: { minWidth: 240, maxWidth: 360 } }}
        >
          {appsLoading && (
            <MenuItem disabled sx={{ justifyContent: 'center' }}>
              <CircularProgress size={18} />
            </MenuItem>
          )}
          {!appsLoading && creditApps.length === 0 && (
            <MenuItem disabled>No credit apps for this customer</MenuItem>
          )}
          {!appsLoading && creditApps.map(app => {
            const status = app.status || 'pending';
            return (
              <MenuItem
                key={app.id}
                onClick={() => { setAppsAnchor(null); StateManager.openCreditAppModal(app.id); }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Chip
                  size="small"
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  sx={{ backgroundColor: STATUS_COLORS[status] || '#9e9e9e', color: '#fff', fontWeight: 600, fontSize: '0.6rem', height: 18 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {`${app.first_name || ''} ${app.last_name || ''}`.trim() || 'Credit App'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {app.car_title ? `${app.car_title}${app.stock ? ` (#${app.stock})` : ''} · ` : ''}
                    {app.time ? moment(app.time, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY') : ''}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Box ref={inputWrapperRef}>
        <TextField
          fullWidth
          size="small"
          label={`Search ${formatTitle(type)}`}
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); search(e.target.value); }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled || loading}
          autoComplete="off"
          InputProps={{ endAdornment: loading ? <CircularProgress size={16} /> : null }}
        />
        <CustomerSelectList
          results={results}
          selected={selected}
          onSelect={(item) => item.action?.()}
          anchorEl={inputWrapperRef.current}
        />
      </Box>

      <NewCustomerModal
        open={newCustomerOpen}
        onClose={() => setNewCustomerOpen(false)}
        stockNumber={stockNumber}
        type={type}
        initialName={pendingName}
        onSaved={(cust) => {
          setInputValue(cust.display_name);
          setCommittedName(cust.display_name);
          StateManager.updateCar({ [type]: cust });
        }}
      />
    </Box>
  );
}

const formatTitle = (raw) => raw.split('-').join(' ').replace(/^\w/, c => c.toUpperCase());

const getLabel = (hit) => {
  if (hit._highlightResult) {
    const h = hit._highlightResult;
    const fn = (h.first_name?.value || hit.first_name || '').replace(/<em>/g, '<strong>').replace(/<\/em>/g, '</strong>');
    const ln = (h.last_name?.value || hit.last_name || '').replace(/<em>/g, '<strong>').replace(/<\/em>/g, '</strong>');
    return `${fn} ${ln}`.trim();
  }
  return `${hit.first_name || ''} ${hit.last_name || ''}`.trim();
};

const removeCustomer = async (stockNumber, type) => {
  const removal = { [type]: firebase.firestore.FieldValue.delete() };
  await Promise.all([
    firebase.firestore().doc('deals/' + stockNumber).set(removal, { merge: true }),
    firebase.firestore().doc('cars/' + stockNumber).set(removal, { merge: true }),
  ]);
  StateManager.updateCar({ [type]: null });
};

const setCustomer = async (stockNumber, type, id) => {
  if (!constants.isValidCustomerId(id)) throw new Error('Customer not found');
  const db = firebase.firestore();
  await Promise.all([
    db.doc('deals/' + stockNumber).set({ [type]: id }, { merge: true }),
    db.doc('cars/' + stockNumber).set({ [type]: id }, { merge: true }),
  ]);
  const snap = await db.doc(`customers/${id}`).get();
  if (!snap.exists) throw new Error('Customer not found');
  const data = snap.data();
  const customer = {
    ...data,
    id,
    display_name: `${data.first_name || ''}${data.last_name ? ' ' : ''}${data.last_name || ''}`.trim(),
  };
  StateManager.updateCar({ [type]: customer });
  return customer;
};
